<template>
    <div class="form-section">
        <div class="section-header">
            <div class="section-number">9</div>
            <h3>Line of Business</h3>
        </div>

        <!-- Business Search and Selection -->
        <div class="business-search-card">
            <div class="search-header">
                <div class="search-title">
                    <i class="bi bi-search"></i>
                    <h4>Search Business Activities</h4>
                </div>
                <div class="search-controls">
                    <div class="search-input-group">
                        <input type="text" class="form-control modern-input search-input"
                            placeholder="Search for business activities, industries, or categories..."
                            v-model="businessSearch" @input="searchBusinessActivities" ref="searchInput" />
                        <button v-if="businessSearch" class="btn-clear-search" @click="clearSearch" type="button"
                            title="Clear search">
                            <i class="bi bi-x-lg"></i>
                        </button>
                    </div>
                </div>
            </div>

            <!-- Quick Categories -->
            <!-- <div class="categories-section" v-if="!businessSearch">
                <h5>Popular Categories</h5>
                <div class="categories-grid">
                    <button v-for="category in popularCategories" :key="category.id" class="category-chip"
                        @click="selectCategory(category)">
                        <i :class="category.icon"></i>
                        {{ category.name }}
                    </button>
                </div>
            </div> -->

            <!-- Search Results -->
            <div class="search-results" v-if="filteredBusinessActivities.length > 0">
                <div class="results-header">
                    <span class="results-count">{{ filteredBusinessActivities.length }} activities found</span>
                    <div class="view-toggle">
                        <button type="button" :class="['view-btn', { active: viewMode === 'grid' }]"
                            @click="viewMode = 'grid'">
                            <i class="bi bi-grid"></i>
                        </button>
                        <button type="button" :class="['view-btn', { active: viewMode === 'list' }]"
                            @click="viewMode = 'list'">
                            <i class="bi bi-list"></i>
                        </button>
                    </div>
                </div>

                <div :class="['business-activities', viewMode]" style="max-height: 300px; overflow-y: auto">
                    <div v-for="activity in filteredBusinessActivities" :key="`lob${activity.lob_id}`"
                        :class="['business-activity-card', { selected: isActivitySelected(activity.lob_id) }]"
                        @click="selectBusinessActivity(activity)">
                        <div class="activity-header">
                            <div class="activity-icon">
                                <i :class="activity.icon"></i>
                            </div>
                            <div class="activity-info">
                                <h6 class="activity-name">{{ activity.line_of_business }}</h6>
                                <p class="activity-category">{{ activity.sub_category }}</p>
                            </div>
                            <div class="activity-actions">
                                <i
                                    :class="['bi', isActivitySelected(activity.lob_id) ? 'bi-check-circle-fill' : 'bi-plus-circle']"></i>
                            </div>
                        </div>
                        <p class="activity-description">{{ activity.description }}</p>
                        <div class="activity-tags">
                            <span class="activity-tag" v-for="tag in activity.tags" :key="tag">{{ tag }}</span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Empty State -->
            <div class="empty-state" v-else-if="businessSearch">
                <i class="bi bi-inbox"></i>
                <p>No business activities found for "{{ businessSearch }}"</p>
                <button class="btn btn-outline-secondary" @click="clearSearch">
                    Clear Search
                </button>
            </div>
        </div>

        <!-- Selected Businesses -->
        <div class="selected-businesses" v-if="selectedBusinesses.length > 0">
            <div class="selected-header">
                <h4>Selected Business Activities</h4>
                <span class="selected-count">{{ selectedBusinesses.length }} selected</span>
            </div>

            <div class="business-list">
                <div v-for="(business, index) in selectedBusinesses" :key="business.lob_id"
                    class="selected-business-card">
                    <div class="business-main-info">
                        <div class="business-icon">
                            <i :class="business.icon"></i>
                        </div>
                        <div class="business-details">
                            <h5>{{ business.line_of_business }} <span class="badge"
                                    :class="business.type === 'New' ? 'bg-danger' : 'bg-secondary'">{{ business.type
                                    }}</span></h5>
                            <p class="business-category">{{ business.sub_category }}</p>
                            <div class="business-stats">
                                <span class="stat">
                                    <strong>{{ business.type === 'New' ? 'Capitalization:' : 'Gross:' }} </strong> ₱{{
                                        formatCurrency(business?.capitalization) }}
                                </span>
                                <!-- <span class="stat">
                                    <strong>Gross:</strong> ₱{{ formatCurrency(business?.estimatedGross) }}
                                </span> -->
                                <span class="stat" v-if="business.employeeCount">
                                    <strong>Employees:</strong> {{ business?.employeeCount }}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div class="business-actions">
                        <button type="button" class="btn-edit-business" @click.stop.prevent="editBusinessDetails(index)"
                            title="Edit details">
                            <i class="bi bi-pencil"></i>
                        </button>
                        <button type="button" class="btn-remove-business"
                            @click.stop.prevent="removeBusinessActivity(index)" title="Remove activity">
                            <i class="bi bi-trash"></i>
                        </button>
                    </div>
                </div>
            </div>
        </div>

    </div>
    <ChildModal :show="showBusinessModal" @close="toggleBusinessModal">
        <LineOfBusinessMoreInfo :apptype="apptype" @close="close" :selected-business="selectedBusiness" :mode="modalMode"
            @save="saveBusinessDetails" />
    </ChildModal>

</template>

<script setup>
import { computed, ref, onMounted, nextTick, onUnmounted, watch } from 'vue';
import LineOfBusinessMoreInfo from './LineOfBusinessMoreInfo.vue';
import ChildModal from '../modals/ChildModal.vue';
import businessService from '@/services/businessService';
import toastService from '@/services/toastService';

// eslint-disable-next-line
const props = defineProps({
    lobs: {
        type: Array,
        default: () => []
    },
    apptype: {
        type: String,
        default: () => "New"
    }
})

const close = ()=>{
   toggleBusinessModal();
}

// eslint-disable-next-line
const emit = defineEmits(['lob-modified','apptype-required'])

// Add to your script section
const businessSearch = ref('');
const viewMode = ref('grid');
const showBusinessModal = ref(false);
const selectedBusiness = ref({ type: props.apptype, capitalization: 0 });
const modalMode = ref('add'); // 'add' or 'edit'
const editingIndex = ref(-1);
const searchInput = ref(null);

const toggleBusinessModal = () => {
    showBusinessModal.value = !showBusinessModal.value;
}

// const popularCategories = [
//     { id: 1, name: 'Retail & Trade', icon: 'bi bi-cart' },
//     { id: 2, name: 'Food & Beverage', icon: 'bi bi-cup-straw' },
//     { id: 3, name: 'Services', icon: 'bi bi-tools' },
//     { id: 4, name: 'Manufacturing', icon: 'bi bi-gear' },
//     { id: 5, name: 'Professional', icon: 'bi bi-briefcase' },
//     { id: 6, name: 'Technology', icon: 'bi bi-laptop' }
// ];

const getAllLobs = async () => {
    if (businessActivities.value === null || businessActivities.value.length === 0)
        businessActivities.value = await businessService.getAllLobs();
}

const businessActivities = ref([
]);

const selectedBusinesses = ref([]);
const businessDetails = ref({
    capitalization: 0,
    estimatedGross: 0,
    employeeCount: 0,
    businessArea: 0,
    specialRequirements: ''
});

const filteredBusinessActivities = computed(() => {
    if (!businessSearch.value) return businessActivities.value;

    const searchTerm = businessSearch.value.toLowerCase();
    return businessActivities.value.filter(activity =>
        activity.line_of_business.toLowerCase().includes(searchTerm) ||
        activity.sub_category.toLowerCase().includes(searchTerm));
});

const searchBusinessActivities = () => {
    // Debounced search logic can be added here
};

const clearSearch = () => {
    businessSearch.value = '';
    // Focus back on search input after clearing
    nextTick(() => {
        if (searchInput.value) {
            searchInput.value.focus();
        }
    });
};

// const selectCategory = (category) => {
//     businessSearch.value = category.name;
//     // Focus on search input after category selection
//     nextTick(() => {
//         if (searchInput.value) {
//             searchInput.value.focus();
//         }
//     });
// };

const isActivitySelected = (activityId) => {
    return selectedBusinesses.value.some(business => business.lob_id === activityId);
};

const selectBusinessActivity = (activity) => {
    if (isActivitySelected(activity.lob_id)) {
        // Already selected, remove it
        const index = selectedBusinesses.value.findIndex(b => b.lob_id === activity.lob_id);
        selectedBusinesses.value.splice(index, 1);
    } else {
        if (!props.apptype) {
            toastService.error("Please select application type first.");
            emit('apptype-required');
            return;
        }
        // New selection, open modal for details
        selectedBusiness.value = { ...activity, type: props.apptype || 'New' };
        modalMode.value = 'add';
        resetBusinessDetails();
        showBusinessModal.value = true;
    }
};

const editBusinessDetails = (index) => {
    editingIndex.value = index;
    selectedBusiness.value = { ...selectedBusinesses.value[index] };
    modalMode.value = 'edit';
    showBusinessModal.value = true;
};

//eslint-disable-next-line
const saveBusinessDetails = (details) => {
    const businessWithDetails = {
        ...selectedBusiness.value,
        no_units: details.no_units,
        product_services: details.product_services,
        capitalization: details.capitalization,
        ...(selectedBusiness.value.description && {
            line_of_business: selectedBusiness.value.description
        })
    };
    if (modalMode.value === 'add') {
        selectedBusinesses.value.push(businessWithDetails);
        emit('lob-modified', selectedBusinesses.value);
    } else {
        selectedBusinesses.value[editingIndex.value] = businessWithDetails;
    }

    closeBusinessModal();
};

const removeBusinessActivity = (index) => {
    selectedBusinesses.value.splice(index, 1);
    emit('lob-modified', selectedBusinesses.value)
};

const closeBusinessModal = () => {
    showBusinessModal.value = false;
    selectedBusiness.value = null;
    editingIndex.value = -1;
    resetBusinessDetails();
};

const resetBusinessDetails = () => {
    businessDetails.value = {
        capitalization: 0,
        estimatedGross: 0,
        employeeCount: 0,
        businessArea: 0,
        specialRequirements: ''
    };
};

watch(() => props.lobs, (newVal) => {
    selectedBusinesses.value = newVal;
}, { immediate: true, deep: true });

const formatCurrency = (amount, options = {}) => {
    const defaultOptions = {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        ...options
    };

    return new Intl.NumberFormat('en-PH', defaultOptions).format(amount);
};

// Keyboard shortcuts
onMounted(() => {
    const handleKeydown = (event) => {
        // Clear search on Escape key
        if (event.key === 'Escape' && businessSearch.value) {
            clearSearch();
            event.preventDefault();
        }

        // Focus search on Ctrl+K or Cmd+K
        if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
            event.preventDefault();
            if (searchInput.value) {
                searchInput.value.focus();
            }
        }
    };
    getAllLobs();

    window.addEventListener('keydown', handleKeydown);

    // Cleanup
    onUnmounted(() => {
        window.removeEventListener('keydown', handleKeydown);
    });
});

</script>

<style scoped>
/* Enhanced Search Input Group */
.search-input-group {
    position: relative;
    width: 400px;
}

.search-input {
    padding-left: 2.5rem;
    padding-right: 2.5rem;
    width: 100%;
    transition: all 0.3s ease;
}

.search-input:focus {
    border-color: #dc3545;
    box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
    transform: translateY(-1px);
}

.search-icon {
    position: absolute;
    left: 1rem;
    top: 50%;
    transform: translateY(-50%);
    color: #6c757d;
    transition: color 0.3s ease;
}

.search-input:focus+.search-icon {
    color: #dc3545;
}

/* Clear Search Button */
.btn-clear-search {
    position: absolute;
    right: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    background: rgba(108, 117, 125, 0.2);
    border: none;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s ease;
    color: #6c757d;
    font-size: 0.75rem;
}

.btn-clear-search:hover {
    background: #dc3545;
    color: white;
    transform: translateY(-50%) scale(1.1);
}

/* Keyboard Shortcut Hint */
.search-input::placeholder {
    transition: all 0.3s ease;
}

.search-input:focus::placeholder {
    opacity: 0.7;
}

/* Responsive enhancements */
@media (max-width: 768px) {
    .search-input-group {
        width: 100%;
    }

    .btn-clear-search {
        right: 0.5rem;
    }
}

/* Rest of your existing CSS remains the same */
.form-section {
    background: white;
    border-radius: 15px;
    padding: 2rem;
    margin-bottom: 2rem;
    box-shadow: 0 5px 25px rgba(220, 53, 69, 0.08);
    border: 1px solid rgba(220, 53, 69, 0.05);
    transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.form-section:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 30px rgba(220, 53, 69, 0.12);
}

.section-header {
    display: flex;
    align-items: center;
    margin-bottom: 2rem;
    padding-bottom: 1rem;
    border-bottom: 2px solid rgba(220, 53, 69, 0.1);
}

.section-number {
    background: linear-gradient(135deg, #dc3545, #c82333);
    color: white;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    margin-right: 1rem;
    font-size: 1.2rem;
}

.section-header h3 {
    color: #2c3e50;
    font-weight: 600;
    margin: 0;
}

.business-search-card {
    background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
    border-radius: 16px;
    padding: 2rem;
    margin-bottom: 2rem;
    border: 2px solid rgba(220, 53, 69, 0.1);
}

.search-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;
}

.search-title {
    display: flex;
    align-items: center;
    gap: 1rem;
}

.search-title i {
    font-size: 1.5rem;
    color: #dc3545;
}

.search-title h4 {
    margin: 0;
    color: #2c3e50;
}

.categories-section {
    margin-bottom: 2rem;
}

.categories-section h5 {
    margin-bottom: 1rem;
    color: #2c3e50;
}

.categories-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
}

.category-chip {
    background: white;
    border: 2px solid #e9ecef;
    border-radius: 12px;
    padding: 1rem;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    cursor: pointer;
    transition: all 0.3s ease;
    font-weight: 500;
}

.category-chip:hover {
    border-color: #dc3545;
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(220, 53, 69, 0.1);
}

.category-chip i {
    color: #dc3545;
    font-size: 1.2rem;
}

.search-results {
    margin-top: 2rem;
}

.results-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;
}

.results-count {
    color: #6c757d;
    font-weight: 500;
}

.view-toggle {
    display: flex;
    gap: 0.5rem;
    background: #f8f9fa;
    padding: 0.25rem;
    border-radius: 8px;
}

.view-btn {
    background: transparent;
    border: none;
    padding: 0.5rem;
    border-radius: 6px;
    cursor: pointer;
    color: #6c757d;
    transition: all 0.3s ease;
}

.view-btn.active {
    background: #dc3545;
    color: white;
}

.business-activities.grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 1.5rem;
}

.business-activities.list {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

.business-activity-card {
    background: white;
    border: 2px solid #e9ecef;
    border-radius: 12px;
    padding: 1.5rem;
    cursor: pointer;
    transition: all 0.3s ease;
    position: relative;
}

.business-activity-card:hover {
    border-color: #dc3545;
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(220, 53, 69, 0.1);
}

.business-activity-card.selected {
    border-color: #dc3545;
    background: linear-gradient(135deg, #fff5f5, #ffe6e6);
}

.activity-header {
    display: flex;
    align-items: flex-start;
    gap: 1rem;
    margin-bottom: 1rem;
}

.activity-icon {
    width: 50px;
    height: 50px;
    background: linear-gradient(135deg, #dc3545, #c82333);
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 1.5rem;
    flex-shrink: 0;
}

.activity-info {
    flex: 1;
}

.activity-name {
    margin: 0 0 0.25rem 0;
    color: #2c3e50;
    font-weight: 600;
}

.activity-category {
    margin: 0;
    color: #6c757d;
    font-size: 0.9rem;
}

.activity-actions i {
    color: #28a745;
    font-size: 1.5rem;
}

.activity-description {
    color: #495057;
    font-size: 0.9rem;
    margin-bottom: 1rem;
    line-height: 1.5;
}

.activity-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
}

.activity-tag {
    background: #f8f9fa;
    color: #495057;
    padding: 0.25rem 0.75rem;
    border-radius: 20px;
    font-size: 0.8rem;
    font-weight: 500;
}

.selected-businesses {
    margin-top: 2rem;
}

.selected-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;
    padding-bottom: 1rem;
    border-bottom: 2px solid rgba(220, 53, 69, 0.1);
}

.selected-count {
    background: linear-gradient(135deg, #dc3545, #c82333);
    color: white;
    padding: 0.5rem 1rem;
    border-radius: 20px;
    font-size: 0.85rem;
    font-weight: 600;
}

.business-list {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

.selected-business-card {
    background: white;
    border-radius: 12px;
    padding: 1.5rem;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
    display: flex;
    justify-content: space-between;
    align-items: center;
    transition: all 0.3s ease;
}

.selected-business-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
}

.business-main-info {
    display: flex;
    align-items: center;
    gap: 1rem;
    flex: 1;
}

.business-icon {
    width: 50px;
    height: 50px;
    background: linear-gradient(135deg, #dc3545, #c82333);
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 1.2rem;
}

.business-details h5 {
    margin: 0 0 0.25rem 0;
    color: #2c3e50;
}

.business-category {
    margin: 0 0 0.75rem 0;
    color: #6c757d;
    font-size: 0.9rem;
}

.business-stats {
    display: flex;
    gap: 1.5rem;
    flex-wrap: wrap;
}

.stat {
    color: #495057;
    font-size: 0.9rem;
}

.business-actions {
    display: flex;
    gap: 0.5rem;
}

.btn-edit-business,
.btn-remove-business {
    background: transparent;
    border: none;
    width: 40px;
    height: 40px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s ease;
    font-size: 1rem;
}

.btn-edit-business {
    color: #007bff;
    border: 1px solid #007bff;
}

.btn-edit-business:hover {
    background: #007bff;
    color: white;
}

.btn-remove-business {
    color: #dc3545;
    border: 1px solid #dc3545;
}

.btn-remove-business:hover {
    background: #dc3545;
    color: white;
}

.empty-state {
    text-align: center;
    padding: 3rem;
    color: #6c757d;
}

.empty-state i {
    font-size: 3rem;
    margin-bottom: 1rem;
    opacity: 0.5;
}

@media (max-width: 768px) {
    .search-header {
        flex-direction: column;
        gap: 1rem;
        align-items: stretch;
    }

    .business-activities.grid {
        grid-template-columns: 1fr;
    }

    .selected-business-card {
        flex-direction: column;
        align-items: stretch;
        gap: 1rem;
    }

    .business-stats {
        flex-direction: column;
        gap: 0.5rem;
    }

    .business-actions {
        align-self: flex-end;
    }
}
</style>